import { Component, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';

/**
 * Standard validator-error renderer for reactive-form controls (Fase 0 CRUD/reporting plan,
 * gap A: no hand-rolled form ever showed a validation error, so an invalid submit looked like
 * a no-op). Wire it under every control:
 *
 * ```html
 * <input pInputText id="surname" formControlName="surname" />
 * <emed-field-error [control]="formGroup.controls.surname" label="Cognome"></emed-field-error>
 * ```
 *
 * `label` is optional and only used by the `required`/`email`/`pattern` fallback messages that
 * don't otherwise name the field; omit it and the message stays generic.
 */
@Component({
  selector: 'emed-field-error',
  standalone: false,
  templateUrl: './emed-field-error.component.html'
})
export class EmedFieldErrorComponent {
  @Input() control: AbstractControl | null = null;
  @Input() label?: string;

  public get visible(): boolean {
    return !!this.control && this.control.touched && this.control.invalid;
  }

  public get message(): string | null {
    const errors = this.control?.errors;
    if (!errors) {
      return null;
    }

    const field = this.label ? `${this.label}: ` : '';

    if (errors['required']) {
      return `${field}campo obbligatorio`;
    }
    if (errors['email']) {
      return `${field}indirizzo email non valido`;
    }
    if (errors['pattern']) {
      return `${field}formato non valido`;
    }
    // Chiave emessa da `codiceFiscaleValidator()` della shared library.
    if (errors['invalidFormat']) {
      return `${field}formato non valido`;
    }
    if (errors['maxlength']) {
      return `${field}lunghezza massima ${errors['maxlength'].requiredLength} caratteri`;
    }
    if (errors['minlength']) {
      return `${field}lunghezza minima ${errors['minlength'].requiredLength} caratteri`;
    }
    if (errors['min']) {
      return `${field}valore minimo ${errors['min'].min}`;
    }
    if (errors['max']) {
      return `${field}valore massimo ${errors['max'].max}`;
    }

    return `${field}valore non valido`;
  }
}
